import { useEffect, useRef, useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { Clock, ArrowRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePriceLock } from "../../contexts/PriceLockContext";
import FloatingToast from "./FloatingToast";

const AMBER = "#D97706";
const NAV_SAFE_TOP_OFFSET = "5rem";

function getRemainingSeconds(expiresAt: string) {
  const expiresAtMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) return 0;
  return Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
}

function formatCountdown(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

const HIDDEN_PATHS = ["/book/cleaning/general-cleaning"];

export default function PriceLockToast() {
  const { lockedPricing } = usePriceLock();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const prevLockRef = useRef(lockedPricing);

  useEffect(() => {
    if (lockedPricing && lockedPricing !== prevLockRef.current) {
      setDismissed(false);
    }
    prevLockRef.current = lockedPricing;
  }, [lockedPricing]);

  useEffect(() => {
    // A null lockedUntil means the lock never expires — nothing to count down,
    // so leave the toast hidden (isHidden already treats 0 as "no countdown").
    if (!lockedPricing?.lockedUntil) {
      setRemainingSeconds(0);
      return undefined;
    }
    const lockedUntil = lockedPricing.lockedUntil;
    function tick() {
      setRemainingSeconds(getRemainingSeconds(lockedUntil));
    }
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [lockedPricing]);

  const isHidden =
    !lockedPricing ||
    lockedPricing.lockFlow === "reschedule" ||
    dismissed ||
    remainingSeconds <= 0 ||
    HIDDEN_PATHS.some((p) => pathname.startsWith(p));
  const isRescheduleLock = lockedPricing?.lockFlow === "reschedule";
  const descriptionText = isRescheduleLock ? "Complete reschedule" : "Complete booking";

  return (
    <FloatingToast
      visible={!isHidden}
      toastKey="price-lock-toast"
      accentColor={AMBER}
      icon={<Clock size={15} color="white" />}
      title="Price locked"
      badge={
        <Text
          rounded="999px"
          bg="white"
          border="1px solid rgba(146,64,14,0.18)"
          px="0.45rem"
          py="0.1rem"
          fontSize="0.68rem"
          fontWeight="900"
          lineHeight="1"
          color="#92400E"
        >
          {formatCountdown(remainingSeconds)}
        </Text>
      }
      description={
        <Flex align="center" gap="0.25rem">
          <Text>{descriptionText}</Text>
          <ArrowRight size={11} color="#64748b" />
        </Flex>
      }
      top={NAV_SAFE_TOP_OFFSET}
      zIndex={9996}
      onClick={() => {
        if (isRescheduleLock) {
          const params = new URLSearchParams();
          if (lockedPricing?.rescheduleSessionId) {
            params.set("dialogSession", String(lockedPricing.rescheduleSessionId));
          }
          if (lockedPricing?.rescheduleBookingId) {
            params.set("dialogBooking", String(lockedPricing.rescheduleBookingId));
          }
          params.set("dialog", "reschedule");
          const query = params.toString();
          navigate(query ? `/my-bookings?${query}` : "/my-bookings");
          return;
        }
        navigate("/book/cleaning/general-cleaning");
      }}
      onDismiss={() => setDismissed(true)}
    />
  );
}
